import { z } from 'zod';
import { appConfig } from '@/config/app-config';
import { HttpFailure, requestJson } from './http';
import { solvePowOffThread } from './pow/pow-client';

const powChallengeSchema = z.object({
  challenge: z.string().min(1),
  difficulty: z.number().int().min(0),
  timestamp: z.number().optional(),
});

const iceServerSchema = z.object({
  urls: z.union([z.string(), z.array(z.string())]),
  username: z.string().optional(),
  credential: z.string().optional(),
});

const turnCredentialsSchema = z.object({
  iceServers: z.array(iceServerSchema).min(1),
  iceCandidatePoolSize: z.number().optional(),
  iceTransportPolicy: z.string().optional(),
});

export type IceServers = readonly RTCIceServer[];

export interface IceConfiguration {
  iceServers: IceServers;
  /** Чи вдалося отримати ретрансляцію, чи це деградація на публічні STUN. */
  hasRelay: boolean;
}

/**
 * Строк дії креденшлів на сервері — 3600 с. Беремо з запасом, щоб не
 * потрапити в дзвінок із креденшлами, що протухнуть у процесі.
 */
const CREDENTIALS_TTL_MS = 3_600_000;
const TTL_SAFETY_MARGIN_MS = 300_000;

/** Пауза після 429. Ліміт на сервері спільний, тож поспіх шкодить усім. */
const RATE_LIMIT_BACKOFF_MS = [60_000, 120_000, 300_000] as const;

const stunOnlyConfiguration = (): IceConfiguration => ({
  iceServers: appConfig.fallbackStunUrls.map((urls) => ({ urls })),
  hasRelay: false,
});

interface CacheEntry {
  configuration: IceConfiguration;
  expiresAt: number;
}

/**
 * Доступ до ретрансляції: доказ роботи, отримання ефемерних креденшлів і кеш.
 *
 * ⚠️ Кеш тут — не оптимізація. Rate-limit `/api/turn-credentials` рахується по
 * IP, який бачить FastAPI, а це завжди nginx (`127.0.0.1`): 10 запитів за 60 с
 * діляться між УСІМА користувачами сервісу. Клієнт, що смикає endpoint на
 * кожен ретрай ICE, кладе видачу креденшлів усім одразу.
 *
 * Кеш свідомо живе в памʼяті вкладки, а не в localStorage: креденшли
 * короткоживучі, і розкладати їх по диску немає навіщо.
 */
export class TurnAccess {
  private cache: CacheEntry | null = null;
  private inFlight: Promise<IceConfiguration> | null = null;
  private rateLimitedUntil = 0;
  private consecutiveRateLimits = 0;

  constructor(private readonly now: () => number = () => Date.now()) {}

  /** Скільки разів клієнт справді сходив по креденшли. Для перевірок. */
  fetchCount = 0;

  async getConfiguration(): Promise<IceConfiguration> {
    const cached = this.cache;
    if (cached && cached.expiresAt > this.now()) {
      return cached.configuration;
    }

    if (this.now() < this.rateLimitedUntil) {
      return stunOnlyConfiguration();
    }

    // Кілька паралельних спроб дзвінка не мають перетворюватись на кілька
    // запитів: перша веде, решта чекає на її результат.
    this.inFlight ??= this.obtain().finally(() => {
      this.inFlight = null;
    });

    return this.inFlight;
  }

  private async obtain(): Promise<IceConfiguration> {
    this.fetchCount += 1;
    try {
      const challenge = await requestJson(appConfig.powChallengeUrl, powChallengeSchema);

      const solution = await solvePowOffThread(challenge.challenge, challenge.difficulty);
      if (solution.status !== 'solved') {
        return stunOnlyConfiguration();
      }

      const credentials = await requestJson(
        appConfig.turnCredentialsUrl,
        turnCredentialsSchema,
        {
          method: 'POST',
          // `difficulty` не надсилаємо: сервер бере власну збережену, а поле
          // із запиту ігнорує.
          body: { challenge: challenge.challenge, nonce: solution.nonce },
        },
      );

      const configuration: IceConfiguration = {
        iceServers: credentials.iceServers.map((server) => ({
          urls: server.urls,
          ...(server.username === undefined ? {} : { username: server.username }),
          ...(server.credential === undefined ? {} : { credential: server.credential }),
        })),
        hasRelay: credentials.iceServers.some((server) =>
          (Array.isArray(server.urls) ? server.urls : [server.urls]).some((url) =>
            url.startsWith('turn:'),
          ),
        ),
      };

      this.consecutiveRateLimits = 0;
      this.cache = {
        configuration,
        expiresAt: this.now() + CREDENTIALS_TTL_MS - TTL_SAFETY_MARGIN_MS,
      };
      return configuration;
    } catch (error) {
      if (error instanceof HttpFailure && error.kind === 'rate-limited') {
        const index = Math.min(this.consecutiveRateLimits, RATE_LIMIT_BACKOFF_MS.length - 1);
        this.rateLimitedUntil = this.now() + (RATE_LIMIT_BACKOFF_MS[index] ?? 60_000);
        this.consecutiveRateLimits += 1;
      }
      // Будь-яка невдача — не привід зривати дзвінок: пробуємо на публічних
      // STUN і повідомляємо користувача лише якщо дзвінок справді не встав.
      return stunOnlyConfiguration();
    }
  }

  /** Час, до якого клієнт не турбуватиме сервер після 429. */
  get backoffUntil(): number {
    return this.rateLimitedUntil;
  }
}
